import * as Sentry from "@sentry/nextjs";

// Shared helpers for the app's error boundaries (global-error.tsx and the
// segment error.tsx files). Everything here must be safe to call while a
// boundary is rendering: none of these functions may throw, because an error
// thrown by an error boundary escapes to Next's built-in fallback ("Application
// error: a client-side exception has occurred"), which is in English, not
// user friendly, and skips our own Sentry reporting.

const CHUNK_RELOAD_FLAG = "orquesti:chunk-load-error-reload";

// Errors already sent to Sentry. A boundary can render more than once for the
// same error (React retries, StrictMode in development), and the same error can
// be seen by a segment boundary and then by global-error.
const reportedErrors = new WeakSet<object>();

/**
 * Chunk load errors happen when a tab that stayed open across a deploy asks for
 * a JS chunk of the previous build, which the current deployment no longer has.
 */
export function isChunkLoadError(error: unknown): boolean {
  if (!(error instanceof Error)) return false;
  return (
    error.name === "ChunkLoadError" ||
    /ChunkLoadError|Loading chunk [\w-]+ failed|Loading CSS chunk/.test(
      error.message || "",
    )
  );
}

/**
 * Reloads the page once per session to recover from a chunk load error. A hard
 * reload fetches the current build. Returns true when a reload was scheduled,
 * false when one was already attempted (or storage is unavailable), so the
 * caller can show its error UI instead of reloading forever.
 */
export function reloadOnceForChunkError(): boolean {
  if (typeof window === "undefined") return false;
  try {
    // sessionStorage throws SecurityError when storage is blocked, which
    // happens in some in-app browsers and with cookies disabled.
    if (window.sessionStorage.getItem(CHUNK_RELOAD_FLAG)) {
      // Clear the flag so a future deploy can retry.
      window.sessionStorage.removeItem(CHUNK_RELOAD_FLAG);
      return false;
    }
    window.sessionStorage.setItem(CHUNK_RELOAD_FLAG, "1");
  } catch {
    // Without storage we cannot guard against a reload loop, so do not reload.
    return false;
  }
  window.setTimeout(() => window.location.reload(), 0);
  return true;
}

/**
 * Sends an error caught by an error boundary to Sentry. In production Next only
 * logs errors caught by an explicit error boundary to the console, so without
 * this call they never reach Sentry. Never throws.
 */
export function reportBoundaryError(
  error: unknown,
  source: string,
  extra?: Record<string, unknown>,
): string | undefined {
  try {
    if (error !== null && typeof error === "object") {
      if (reportedErrors.has(error)) return undefined;
      reportedErrors.add(error);
    }
    const digest =
      error !== null && typeof error === "object" && "digest" in error
        ? String((error as { digest?: unknown }).digest ?? "")
        : "";
    return Sentry.captureException(error, {
      level: "error",
      tags: {
        source,
        page:
          typeof window !== "undefined" ? window.location.pathname : undefined,
        chunkLoadError: isChunkLoadError(error) ? "true" : "false",
      },
      extra: {
        ...extra,
        digest: digest || undefined,
      },
    });
  } catch (captureError) {
    try {
      console.error("[ErrorBoundary] Failed to report error:", captureError);
    } catch {
      // Nothing else to do.
    }
    return undefined;
  }
}
